import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  Divider,
  Grid,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { formatDateEuropean } from '../../utils/dateUtils';
import { TYPES, documentLabel } from './FinanceCreateWizard';
import { canAccess } from '../../utils/permissions';

function allocationLabel(line, t, projects) {
  const alloc = (line.allocations || [])[0];
  if (!alloc) return t('finance.unallocated');
  if (alloc.locationKind === 'UNASSIGNED') return t('finance.notProjectRelated');
  if (alloc.locationKind === 'WAREHOUSE') return t('finance.warehouse');
  const project = projects.find((p) => Number(p.id) === Number(alloc.projectId));
  return project ? project.name : t('finance.project');
}

function allocationSummary(detail, t, projects) {
  const buckets = { indirect: 0, warehouse: 0, projects: {} };
  (detail.lines || []).forEach((line) => {
    (line.allocations || []).forEach((alloc) => {
      const amount = Number(alloc.amount) || 0;
      if (alloc.locationKind === 'UNASSIGNED') buckets.indirect += amount;
      else if (alloc.locationKind === 'WAREHOUSE') buckets.warehouse += amount;
      else if (alloc.projectId) {
        const key = String(alloc.projectId);
        buckets.projects[key] = (buckets.projects[key] || 0) + amount;
      }
    });
  });
  const rows = Object.entries(buckets.projects).map(([id, amount]) => {
    const project = projects.find((p) => String(p.id) === id);
    return `${project?.name || id}: ${amount}`;
  });
  if (buckets.indirect) rows.push(`${t('finance.notProjectRelated')}: ${buckets.indirect}`);
  if (buckets.warehouse) rows.push(`${t('finance.warehouse')}: ${buckets.warehouse}`);
  return rows;
}

export default function FinanceDocumentDetail({
  t,
  type,
  user,
  canWrite,
  canPrices,
  detail,
  audit,
  projects,
  linkSearchType,
  setLinkSearchType,
  linkOptions,
  searchRelated,
  onLink,
  onDownload,
  onPaid,
  onStatus,
  onArchive,
}) {
  const [action, setAction] = useState(null);
  const [detailLink, setDetailLink] = useState(null);
  const [quoteStatus, setQuoteStatus] = useState(detail.quote?.status || 'DRAFT');
  const extraDate = detail.quote?.validUntil || detail.deliveryNote?.receivedAt || detail.invoice?.dueDate;
  const extraDateLabel = type === 'QUOTE' ? t('finance.validUntil') : type === 'DELIVERY_NOTE' ? t('finance.deliveryDate') : t('finance.dueDate');
  const summary = allocationSummary(detail, t, projects);

  const coverageTitle = type === 'DELIVERY_NOTE' ? t('finance.receiptsBySite') : t('finance.deliveredWhere');
  const coverageEmpty = type === 'DELIVERY_NOTE' ? t('finance.none') : t('finance.noDeliveriesYet');

  return (
    <Stack spacing={2} mt={1}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('finance.basics')}</Typography>
          <Typography>{t('finance.date')}: {formatDateEuropean(detail.documentDate)}</Typography>
          {extraDate && <Typography>{extraDateLabel}: {formatDateEuropean(extraDate)}</Typography>}
          {detail.quote && (
            <Typography>
              {t('finance.status')}: {detail.quote.status}
              {detail.quote.computed?.expired ? ` (${t('finance.expired')})` : ''}
            </Typography>
          )}
          {detail.invoice && canPrices && <Typography>{t('finance.payment')}: {detail.invoice.paymentStatus}</Typography>}
          {(detail.files || []).length > 0 && (
            <Typography variant="body2">{t('finance.original')}: {(detail.files || []).map((f) => f.originalName).join(', ')}</Typography>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('finance.related')}</Typography>
          {(detail.relatedDocuments || []).length === 0 && <Typography variant="body2">{t('finance.none')}</Typography>}
          <Stack alignItems="flex-start">
            {(detail.relatedDocuments || []).map((rel) => (
              <Button key={rel.id} component={RouterLink} to={`/finance/${rel.type === 'QUOTE' ? 'quotes' : rel.type === 'INVOICE' ? 'invoices' : 'deliveries'}?open=${rel.id}`}>
                {t(`finance.types.${rel.type}`)} {rel.documentNumber || `#${rel.id}`} · {rel.counterpartyName}
              </Button>
            ))}
          </Stack>
        </Grid>
      </Grid>

      <Divider />

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('finance.lines')}</Typography>
          {(detail.lines || []).map((line) => (
            <Box key={line.id}>
              <Typography>
                {line.description} — {line.quantity} {line.unit}
                {canPrices && type !== 'DELIVERY_NOTE' && line.totalAmount != null ? ` · ${line.totalAmount}` : ''}
                {' · '}
                {allocationLabel(line, t, projects)}
              </Typography>
            </Box>
          ))}
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('finance.allocated')}</Typography>
          {summary.length === 0 && <Typography variant="body2">{t('finance.none')}</Typography>}
          {summary.map((row) => <Typography key={row} variant="body2">{row}</Typography>)}
          {detail.unallocated?.unallocatedAmount ? (
            <Typography>{t('finance.unallocated')}: {detail.unallocated.unallocatedAmount}</Typography>
          ) : null}
        </Grid>
      </Grid>

      <Divider />

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{coverageTitle}</Typography>
          {(detail.deliveryCoverage || []).length === 0 && (
            <Typography variant="body2">{coverageEmpty}</Typography>
          )}
          {(detail.deliveryCoverage || []).map((row, idx) => (
            <Box key={`${row.deliveryNoteId}-${idx}`}>
              <Typography variant="body2">
                {row.deliveryNoteNumber || `#${row.deliveryNoteId}`} · {row.locationKind === 'WAREHOUSE' ? t('finance.warehouse') : (row.projectName || t('finance.project'))}
                {row.receivedAt ? ` · ${formatDateEuropean(row.receivedAt)}` : ''}
              </Typography>
              {(row.lines || []).map((line, i) => (
                <Typography key={i} variant="caption" display="block">
                  {line.description}: {line.receivedQuantity} {line.unit || ''}
                </Typography>
              ))}
            </Box>
          ))}
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('finance.audit')}</Typography>
          {audit.map((log) => (
            <Typography key={log.id} variant="caption" display="block">{new Date(log.changedAt).toLocaleString()} · {log.action} · {log.changedByUser?.email}</Typography>
          ))}
        </Grid>
      </Grid>

      {canWrite && <Divider />}

      {canWrite && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" onClick={() => setAction('link')}>{t('finance.linkDocument')}</Button>
          {(detail.files || []).length > 0 && (
            <Button variant="outlined" onClick={() => setAction('original')}>{t('finance.viewOriginal')}</Button>
          )}
          {type === 'INVOICE' && (
            <Button variant="outlined" onClick={() => onPaid()}>
              {detail.invoice?.paymentStatus === 'PAID' ? t('finance.markUnpaid') : t('finance.markPaid')}
            </Button>
          )}
          {type === 'QUOTE' && (
            <Button variant="outlined" onClick={() => { setQuoteStatus(detail.quote?.status || 'DRAFT'); setAction('status'); }}>{t('finance.changeStatus')}</Button>
          )}
          <Button color="error" variant="outlined" onClick={() => setAction('archive')}>{t('finance.archive')}</Button>
        </Stack>
      )}

      <Dialog open={action === 'link'} onClose={() => setAction(null)} fullWidth>
        <DialogTitle>{t('finance.linkDocument')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel>{t('finance.linkType')}</InputLabel>
              <Select label={t('finance.linkType')} value={linkSearchType} onChange={(e) => setLinkSearchType(e.target.value)}>
                {TYPES.filter((item) => item.type !== type && canAccess(user, item.read)).map((item) => (
                  <MenuItem key={item.type} value={item.type}>{t(`finance.types.${item.type}`)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              options={linkOptions}
              getOptionLabel={documentLabel}
              value={detailLink}
              onChange={(_, value) => setDetailLink(value)}
              onInputChange={(_, value) => { searchRelated(value).catch(() => {}); }}
              renderInput={(params) => <TextField {...params} label={t('finance.findDocument')} />}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAction(null)}>{t('common.cancel')}</Button>
          <Button
            disabled={!detailLink}
            variant="contained"
            onClick={async () => {
              await onLink(detailLink.id);
              setDetailLink(null);
              setAction(null);
            }}
          >
            {t('finance.linkNow')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={action === 'original'} onClose={() => setAction(null)}>
        <DialogTitle>{t('finance.viewOriginal')}</DialogTitle>
        <DialogContent>
          <Stack spacing={1} mt={1}>
            {(detail.files || []).map((file) => (
              <Button key={file.id} onClick={() => onDownload(detail.id, file.id, file.originalName)}>
                {file.originalName} v{file.version}
              </Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAction(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={action === 'status'} onClose={() => setAction(null)}>
        <DialogTitle>{t('finance.changeStatus')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>{t('finance.status')}</InputLabel>
            <Select label={t('finance.status')} value={quoteStatus} onChange={(e) => setQuoteStatus(e.target.value)}>
              {['DRAFT', 'ACTIVE', 'ACCEPTED', 'REJECTED', 'CANCELLED', 'CLOSED'].map((s) => (
                <MenuItem key={s} value={s}>{t(`finance.quoteStatus.${s}`)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAction(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={async () => { await onStatus(quoteStatus); setAction(null); }}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={action === 'archive'} onClose={() => setAction(null)}>
        <DialogTitle>{t('finance.archive')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>{t('finance.archiveConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAction(null)}>{t('common.cancel')}</Button>
          <Button color="error" variant="contained" onClick={async () => { await onArchive(); setAction(null); }}>{t('finance.archive')}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
